import React from "react";
import styled from "styled-components";
import { Theme } from "./Theme";

type AppPageFrameProps = {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  centered?: boolean;
  fillViewport?: boolean;
  viewportHeightMode?: "dynamic" | "small";
  maxWidth?: string;
  contentMaxWidth?: string;
  contentPadding?: string;
  maxHeight?: string;
  padding?: string;
};

export const AppPageFrame: React.FC<AppPageFrameProps> = ({
  children,
  className,
  contentClassName,
  centered = true,
  fillViewport = false,
  viewportHeightMode = "dynamic",
  maxWidth = "768px",
  contentMaxWidth = "430px",
  contentPadding = "0",
  maxHeight = "768px",
  padding = "0",
}) => {
  return (
    <Viewport
      $centered={centered}
      $padding={padding}
      $viewportHeightMode={viewportHeightMode}
      className={className}
    >
      <CardShell
        $fillViewport={fillViewport}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $viewportHeightMode={viewportHeightMode}
      >
        <CardBackground />
        <CardContent $fillViewport={fillViewport}>
          <CardContentInner
            $fillViewport={fillViewport}
            $contentMaxWidth={contentMaxWidth}
            $contentPadding={contentPadding}
            className={contentClassName}
          >
            {children}
          </CardContentInner>
        </CardContent>
      </CardShell>
    </Viewport>
  );
};

const getViewportUnit = (mode: "dynamic" | "small") =>
  mode === "small" ? "100svh" : "100dvh";

const Viewport = styled.div<{
  $centered: boolean;
  $padding: string;
  $viewportHeightMode: "dynamic" | "small";
}>`
  width: 100%;
  min-height: 100vh;
  min-height: ${({ $viewportHeightMode }) =>
    getViewportUnit($viewportHeightMode)};
  padding: ${({ $padding }) => $padding};
  display: flex;
  align-items: ${({ $centered }) => ($centered ? "center" : "stretch")};
  justify-content: center;
  background: ${Theme.colors.pageBgColor};
`;

const CardShell = styled.div<{
  $fillViewport: boolean;
  $maxWidth: string;
  $maxHeight: string;
  $viewportHeightMode: "dynamic" | "small";
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth};
  height: ${({ $fillViewport, $maxHeight }) =>
    $fillViewport ? "auto" : `min(calc(100vh - 20px), ${$maxHeight})`};
  height: ${({ $fillViewport, $maxHeight, $viewportHeightMode }) =>
    $fillViewport
      ? "auto"
      : `min(calc(${getViewportUnit($viewportHeightMode)} - 20px), ${$maxHeight})`};
  min-height: ${({ $fillViewport }) => ($fillViewport ? "100vh" : "unset")};
  min-height: ${({ $fillViewport, $viewportHeightMode }) =>
    $fillViewport ? getViewportUnit($viewportHeightMode) : "unset"};
  max-height: ${({ $fillViewport, $maxHeight }) =>
    $fillViewport ? "none" : $maxHeight};
  border-radius: 24px;
  overflow: hidden;
  overscroll-behavior: ${({ $fillViewport }) =>
    $fillViewport ? "auto" : "contain"};
  flex-shrink: 0;
`;

const CardBackground = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background:
    radial-gradient(
      circle at top center,
      rgba(106, 217, 255, 0.1) 0%,
      rgba(106, 217, 255, 0.04) 24%,
      rgba(0, 0, 0, 0) 58%
    ),
    linear-gradient(195deg, #111111 0%, ${Theme.colors.blockColor} 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 18px 48px rgba(0, 0, 0, 0.28);
`;

const CardContent = styled.div<{ $fillViewport: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: ${({ $fillViewport }) => ($fillViewport ? "visible" : "auto")};
  overscroll-behavior: ${({ $fillViewport }) =>
    $fillViewport ? "auto" : "contain"};
  display: flex;
  justify-content: center;
`;

const CardContentInner = styled.div<{
  $fillViewport: boolean;
  $contentMaxWidth: string;
  $contentPadding: string;
}>`
  flex: 1 0 auto;
  width: 100%;
  max-width: ${({ $contentMaxWidth }) => $contentMaxWidth};
  min-height: 100%;
  height: auto;
  padding: ${({ $contentPadding }) => $contentPadding};
  box-sizing: border-box;
`;
